import { Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import EllipsisMenu from "../../../../components/EllipsisMenu/EllipsisMenu";
import THzHeatmapRow from "../../../../components/THzHeatmapRow/THzHeatmapRow";
import AnalysisSection from "../AnalysisSection/AnalysisSection";
import { THzSectionProps } from "./THzSection.types";
import "./THzSection.css"
import useAudioPlayer from "../../../../hooks/useAudioPlayer";
import PlayIcon from "../../../../media/PlayIcon";
import PauseIcon from "../../../../media/PauseIcon";
import { useEffect, useState } from "react";
import SkeletonLoadingView from "../../../../components/SkeletonLoadingView/SkeletonLoadingView";
import { AnalysisGraphData } from "../../../../types/types";

const THzSection = (props: THzSectionProps) => {
  const {
    width,
    colourSamples,
    selectedScan,
    onSelect,
    pixelHeight,
    isolatedScans,
    vectorOverlays,
    ellipsisMenu,
  
    showOverlay,
    minDataPoint,
    maxDataPoint
  } = props

  const [{
    audioRef,
    currentTime,
    duration,
    isPlaying,
    onPlay,
    onPause,
    onCancel
  }] = useAudioPlayer()

  const animationDuration = 300
  const skeletonTHzScanHeight = "2em"
  const skeletonGraphHeight = "15em"
  const colourPalette = "magma"
  const graphLineColours = ["#C9349C", "#6ADCF2", "#8884d8", "#FFA500"]

  const [graphData, setGraphData] = useState<AnalysisGraphData[][]>([])

  useEffect(() => {
    if (colourSamples === undefined) { return }
    const newGraphData = []
    // 3d -> 2d
    // [scan][rows][samples]
    // []
    //
    for (let scanIndex=0; scanIndex<colourSamples.length; scanIndex++) {
      const singelGraphData = []
      for (let i=0; i<colourSamples[scanIndex][0].length; i++) {
        const scan = colourSamples[scanIndex]
        singelGraphData.push({
          sensor1: Math.floor(scan[0][i]),
          sensor2: Math.floor(scan[1][i]),
          sensor3: Math.floor(scan[2][i]),
          sensor4: Math.floor(scan[3][i]),
          amt: i
        })
      }
      newGraphData.push(singelGraphData)
    }
    setGraphData(newGraphData)
  }, [colourSamples])

  useEffect(() => {
    onCancel()
  }, [selectedScan, audioRef, onCancel])

  const getKeyData = (): number[][] => {
    const key = []
    for (let i=0; i<255; i++) {
      key.push(i)
    }
    return [key, key]
  }

  const getHeatmapPixelWidth = () => {
    if (colourSamples === undefined) { return 1 }
    if (colourSamples.length === 0) { return 1 }

    const samples = colourSamples[selectedScan]
    if (samples === undefined) { return 1 }
    if (samples.length === 0) { return 1 }

    return width / samples[0].length
  }

  if (colourSamples === undefined || isolatedScans === undefined) {
    return (
      <AnalysisSection title="THz">
        <>
          <div className="analysis-section thz-section-loading-container">
            {[...Array(8)].map(() => (
              <SkeletonLoadingView style={{height: skeletonTHzScanHeight}}/>
            ))}
          </div>

          <div className="analysis-section thz-section-loading-container">
            <SkeletonLoadingView style={{height: skeletonGraphHeight}} />
          </div>
        
        </>
      </AnalysisSection>
    )
  }

  return (
    <AnalysisSection title="THz" titleChild={
      <EllipsisMenu ellipsisMenu={ellipsisMenu} />
    }>
      <div className="analysis-section">
        <THzHeatmapRow
          colours={getKeyData()}
          colourPalette={colourPalette}
          width={width} 
          pixelSize={{width: width / 255, height: 3}}
        />
        {colourSamples.map((scanColours, key) => (
          <THzHeatmapRow
            key={key}
            colours={scanColours}
            colourPalette={colourPalette}
            vectors={key === 0 ? vectorOverlays : []}
            width={width} 
            pixelSize={{width: getHeatmapPixelWidth(), height: pixelHeight}}
            isSelected={key === selectedScan}
            onSelect={() => onSelect(key)}
            scanInfo={`Scan: ${(isolatedScans[key]?.scan_no ?? 0) + 1} Angle: ${isolatedScans[key]?.angle ?? ""}°`}
            scanNumber={key}
            showOverlay={showOverlay}
            minDataPoint={minDataPoint}
            maxDataPoint={maxDataPoint}
          />
        ))}
      </div>

      <div className="analysis-section">
        {colourSamples !== undefined && (
          <>
            <LineChart
              width={width}
              height={200}
              data={graphData[selectedScan]}
              margin={{
                top: 0,
                right: 0,
                left: 1,
                bottom: 0,
              }}
            >
              <XAxis dataKey="name" />
              <YAxis domain={[0, 270]} tick={false} axisLine={false} width={0} />
              <Tooltip />
              {graphLineColours.map((colour, key) => (
                <Line key={key} animationDuration={animationDuration} type="monotone" dataKey={`sensor${key+1}`} stroke={colour} dot={false} />
              ))}
            </LineChart>
            <THzHeatmapRow width={width} colours={colourSamples[selectedScan] ?? 1} colourPalette={colourPalette} pixelSize={{ width: getHeatmapPixelWidth(), height: 5 }} />
          </>
        )}
      </div>

      <div className="scan-data-container">
        <div className="analysis-section">
          <div className="isolated-scan-data-grid">
            <p>Scan Number:</p>
            <p>{(isolatedScans[selectedScan]?.scan_no ?? 0) + 1}</p>
            <p>Scan Angle:</p>
            <p>{isolatedScans[selectedScan]?.angle ?? 0}°</p>
            <p>Duration:</p>
            <p>{(isolatedScans[selectedScan]?.duration ?? 0) / 1000}s</p>
          </div>
        </div>

        <div className="analysis-section">
          {isolatedScans[selectedScan] !== undefined && isolatedScans[selectedScan]?.comment !== "" ? (
            <div className="voice-comment-container">
              <div className="audio-player-container">
                {isPlaying ? (
                  <div className="voice-comment-play-button" onClick={onPause}>
                    <PauseIcon />
                  </div>
                ) : (
                  <div className="voice-comment-play-button" onClick={onPlay}>
                    <PlayIcon />
                  </div>
                )}

                <p className="audio-comment-time">{`0:${currentTime.toFixed(1)}`}</p>
                <div className="audio-background-bar">
                  <div className="audio-progress" style={{width: `${(currentTime/duration)*100}%`}}></div>
                </div>
                <p className="audio-comment-time">{`0:${duration.toFixed(1)}`}</p>
                <audio ref={audioRef}>
                  <source src={`data:audio/mp3;base64,${isolatedScans[selectedScan]?.comment ?? "none"}`} type="audio/mp3" />
                </audio>
              </div>
            </div>
          ) : (
            <div className="no-analysis-container">
              <p>No audio comment available</p>
            </div>
          )}
        {/* <p className="form-input-label">Scan Comment</p>
        <textarea
          disabled={true}
          className="scan-comment-text"
          value={isolatedScans[selectedScan]?.comment ?? "No comment"}
          placeholder="No comment saved."
        /> */}
        </div>
      </div>
    </AnalysisSection>
  )
}

export default THzSection